import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { gridWidthToPercentage } from '@utils/grid-utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import ProductionPolicyDetailsWidget from './tabs/summary-tab/PolicyDetailsWidget';
import TabsLayout from '@components/layout/tabs/TabsLayout';
import { useAppSelector } from '@redux/hooks';
import { productionGroupPolicyPage } from './content';
import { cloneDeep } from 'lodash';
import {
  getGroupPolicyDetailsQuery,
  getPlanPolicySpecificMedicalCovers,
} from './queries';
import { useLazyQuery } from '@apollo/client';
import { GetProductionPolicyData } from './functions';
import { IProductionDetailsSummary } from '.';
import { getFirePlanCoversLov, getPlanCoversLov, LookupToList } from './utils';
import { MarineGroupCertificateTableWidget } from './tabs/certificates-tab/marine/marine-group-certificate-table/MarineGroupCertificateTableWidget';
import { FleetGroupCertificateTableWidget } from './tabs/certificates-tab/fleet/fleet-certificate-table/FleetCertificateTableWidget';
import PolicyCertficateMarineGroupSummaryDialogShell from './tabs/certificates-tab/marine/marine-group-certificate-summary-popup/PolicyCertficateMarineGroupSummaryDialogShell';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ProductionAmendmentListing from './tabs/amendments-tab/ProductionAmendmentListing';
import { MedicalGroupCertificateTableWidget } from './tabs/certificates-tab/medical/medical-group-certificate-table/MedicalGroupCertificateTableWidget';
import LeftPanel from './sections/LeftPanel';
import CostAndChargesWidget from './tabs/summary-tab/CostAndChargesWidget';
import PolicyCoversWidget from './tabs/summary-tab/PolicyCoversWidget';
import OfferDetailsWidget from './tabs/summary-tab/offer-details/OfferDetailsWidget';
import PolicyPersonsTab from './tabs/policy-persons-tab/PolicyPersonsTab';
import PaymentTermsSchedule from './tabs/payment-terms-tab/payment-terms-schedule/PaymentTermsSchedule';
import CertificatePaymentTermsSchedule from './tabs/payment-terms-tab/certificate-payment-terms-schedule/CertificatePaymentTermsSchedule';
import { CommissionSplitWidget } from './tabs/summary-tab/commission-split/CommissionSplitWidget';
import DocumentsTab from './tabs/documents-tab/DocumentsTab';
import Stepper from './sections/Stepper';
import { useTabsLayoutStyles } from '@styles/sharedStyles';
import {
  GenericLines,
  getGenericLine,
  getLineCategory,
  LineCategory,
} from '@utils/helper-utils';

const GroupProductionPolicyPage: React.FC = () => {
  const { classes } = useTabsLayoutStyles();

  const user = useAppSelector((state) => state.user);

  const [loading, setLoading] = useState<boolean>(true);
  const { id: entityId } = useParams();

  const [data, setData] = useState<IProductionDetailsSummary>();
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({
    matters: {},
  });

  const lineCategory = getLineCategory(data?.LineId?.ExternalCode);
  const genericLine = getGenericLine(data?.LineId?.ExternalCode);

  const [getGroupPolicyDetailsLazy] = useLazyQuery(getGroupPolicyDetailsQuery);
  const [getNonSelectedPlanCoverLazy] = useLazyQuery(
    getPlanPolicySpecificMedicalCovers()
  );

  const [marineCertficateOpen, setMarineCertficateOpen] =
    useState<boolean>(false);
  const certificateActionCount = useRef<number>(0);

  const getPlanCoverDetails = async (
    planId: string,
    planCoverIds: string[]
  ) => {
    const { data } = await getNonSelectedPlanCoverLazy({
      variables: {
        selectedPlanID: planId,
        selectedPlanCoverIDs: planCoverIds,
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    return data;
  };

  const initialize = async () => {
    try {
      setLoading(true);
      const { data: policyDetails } = await getGroupPolicyDetailsLazy({
        variables: { id: entityId, state: 'LATEST' },
        errorPolicy: 'all',
      });

      const summaryData = GetProductionPolicyData(policyDetails);

      if (summaryData.LineId.ExternalCode?.toString() === '6') {
        const planCoverDetails = await getPlanCoverDetails(
          summaryData.PlanID.Id,
          []
        );
        const lovCovers = getPlanCoversLov(planCoverDetails);
        setLovs((prevLovs) => ({
          ...prevLovs,
          policyCovers: lovCovers,
        }));
      }

      const lists = LookupToList(policyDetails);
      setLovs((prevLovs) => ({
        ...prevLovs,
        medicalClasses: lists.medicalClasses,
        matters: lists.matters,
        geoLocations: lists.geoLocations,
        phoneType: lists.phoneType,
        occupationClasses: lists.occupationClasses,
        nationalities: lists.nationalities,
        countries: lists.countries,
        insuredRelations: lists.insuredRelations,
        genders: lists.genders,
      }));

      setData(summaryData);
      setLoading(false);
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const renderTabs = useMemo(() => {
    const tabs = cloneDeep(productionGroupPolicyPage);

    if (!data) {
      return tabs;
    }

    tabs.tabs[0].widgets[0].children = (
      <div className={classes.tabSplitBody}>
        <div className={classes.tabMainBody}>
          <ProductionPolicyDetailsWidget data={data} />
          {data?.Covers?.length > 0 && (
            <PolicyCoversWidget
              currencySymbol={data?.PolicyCurrency?.Symbol || ''}
              data={data.Covers}
              lineExternalCode={Number(data.LineId.ExternalCode)}
            />
          )}
          <CostAndChargesWidget data={data} />
        </div>

        <div className={classes.tabRightSection}>
          <OfferDetailsWidget data={data} />
          <CommissionSplitWidget data={data} />
        </div>
      </div>
    );

    tabs.tabs[1].widgets[0].children = (
      <PolicyPersonsTab
        data={data}
        lovs={lovs}
        planDetails={{
          lineExternalCode: data?.LineId?.ExternalCode,
          PlanMaxNbInsured: data?.PlanID?.MaxNbInsured,
        }}
        onDataRefresh={initialize}
      />
    );

    tabs.tabs[2].widgets[0].children = (
      <div className={classes.tabContents}>
        {lineCategory === LineCategory.Generic &&
          genericLine === GenericLines.MARINE && (
            <MarineGroupCertificateTableWidget
              entityId={data.Id}
              key={`certificate-table-${certificateActionCount.current}`}
              currenySymbol={data?.PolicyCurrency?.Symbol}
              onAddCertActionClicked={() => {
                setMarineCertficateOpen(true);
              }}
            />
          )}
        {lineCategory === LineCategory.Motor && (
          <FleetGroupCertificateTableWidget
            entityId={data.Id}
            currencySymbol={data?.PolicyCurrency?.Symbol}
            lineExternalCode={data.LineId.ExternalCode}
          />
        )}
        {lineCategory === LineCategory.Medical && (
          <MedicalGroupCertificateTableWidget
            entityId={data.Id}
            currencySymbol={data?.PolicyCurrency?.Symbol}
            planDetails={{
              sublineExternalCode: data?.SubLineId?.ExternalCode,
              sublineName: data?.SubLineId?.Name,
              planExternalCode: data?.PlanID?.ExternalCode,
              planName: data?.PlanID?.Name,
            }}
          />
        )}
      </div>
    );

    tabs.tabs[3].widgets[0].children = (
      <DocumentsTab data={data} isGroupPolicy />
    );

    tabs.tabs[4].widgets[0].children = (
      <div className={classes.tabContents}>
        <PaymentTermsSchedule
          policyId={entityId}
          policyNumber={data?.PolicyNumber}
          isGroup={true}
        />
        <CertificatePaymentTermsSchedule policyId={entityId} />
      </div>
    );

    tabs.tabs[5].widgets[0].children = (
      <ProductionAmendmentListing
        policyId={entityId}
        isGroup
        allowEndorsement={user.info.businessUser_AllowEndorsement}
        allowPolicyCorrection={user.info.businessUser_AllowPolicyCorrection}
        transferredToAccounting={data.TransferredToAccounting}
        policyDetails={{
          policyNumber: data.PolicyNumber,
          policyEffectiveDate: data.PolicyEffectiveDate,
          policyExpiryDate: data.PolicyExpiryDate,
          access: user.info?.businessUser_Access?.Title?.toUpperCase(),
        }}
      />
    );

    return tabs;
  }, [data, certificateActionCount.current]);

  return (
    <>
      <TabsLayout
        name="productionPolicyPage"
        layout={renderTabs}
        loading={loading || !data}
        leftChildren={<LeftPanel data={data} isGroup />}
        mainChildren={<Stepper activeStep={data?.Status} />}
      />

      {marineCertficateOpen && (
        <PolicyCertficateMarineGroupSummaryDialogShell
          onClose={() => {
            setMarineCertficateOpen(false);
          }}
          onSuccess={() => {
            certificateActionCount.current++;
            setMarineCertficateOpen(false);
          }}
          data={data}
        />
      )}
    </>
  );
};

export default GroupProductionPolicyPage;
